// File: src/components/pages/BookForStudents.jsx
// Instructor page - Book slots on behalf of students

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, ChevronRight, Clock, Coffee, AlertCircle, CheckCircle, Users, Sparkles } from 'lucide-react';
import { bookingSlotAPI, bookingAPI, studentAPI } from '../../services/api';
import { getMondayOfCurrentWeek } from '../../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK } from '../../utils/constants';
import '../../styles/unified.css';

export default function BookForStudents() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');
  const instructorAuth0Id = user?.sub;

  // State for students
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  // State for booking slots (reused from BookYourSlots)
  const [publishedTemplate, setPublishedTemplate] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMondayOfCurrentWeek());
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingMessage, setBookingMessage] = useState('');
  const [existingBookings, setExistingBookings] = useState(new Set());

  // Load instructor's students on mount
  useEffect(() => {
    if (isServiceProvider && instructorAuth0Id) {
      loadMyStudents();
    }
  }, [isServiceProvider, instructorAuth0Id]);

  // Load instructor's published template when component mounts
  useEffect(() => {
    if (isServiceProvider && instructorAuth0Id) {
      loadMyPublishedTemplate();
    }
  }, [isServiceProvider, instructorAuth0Id]);

  /**
   * Load all students registered under this instructor
   */
  const loadMyStudents = async () => {
    setLoadingStudents(true);
    setStudentsError(null);
    
    try {
      const response = await studentAPI.getMyStudents(getAccessTokenSilently);
      
      if (response.success && response.data) {
        // Filter only active students
        const activeStudents = response.data.filter(student => student.status === 'active');
        setStudents(activeStudents);
        
        if (activeStudents.length === 0) {
          setStudentsError('No active students found. Please invite students first.');
        }
      } else {
        setStudents([]);
        setStudentsError('No students found. Please invite students first.');
      }
    } catch (err) {
      console.error('Failed to load students:', err);
      setStudents([]);
      setStudentsError('Failed to load students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };

  /**
   * Load instructor's own published template
   */
  const loadMyPublishedTemplate = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await bookingSlotAPI.getPublishedTemplate(getAccessTokenSilently);
      
      if (response.success && response.data) {
        console.log('Published template loaded:', response.data);
        setPublishedTemplate(response.data);
        
        // Load existing bookings for this instructor
        await loadExistingBookings(response.data.spPhoneId);
      } else {
        setPublishedTemplate(null);
        setError('Please create and activate a booking template first.');
      }
    } catch (err) {
      console.error('Failed to load template:', err);
      setPublishedTemplate(null);
      setError('Please create and activate a booking template first.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load existing bookings for the instructor to disable already-booked slots
   */
  const loadExistingBookings = async (instructorPhone) => {
    try {
      console.log('Loading existing bookings for instructor:', instructorPhone);
      
      const response = await bookingAPI.getBookingsByInstructorPhone(
        instructorPhone,
        getAccessTokenSilently
      );
      
      if (response.success && response.data) {
        // Create a Set of booked slot keys (date-startTime-endTime)
        const bookedSlots = new Set();
        
        response.data.forEach(booking => {
          // Only include active bookings (not cancelled)
          if (booking.status !== 'cancelled') {
            const slotKey = `${booking.date}-${booking.startTime}-${booking.endTime}`;
            bookedSlots.add(slotKey);
          }
        });
        
        console.log(`Found ${bookedSlots.size} existing bookings`);
        setExistingBookings(bookedSlots);
      }
    } catch (err) {
      console.error('Failed to load existing bookings:', err);
      // Don't block the UI if this fails - just log the error
    }
  };

  /**
   * Handle booking slots on behalf of selected student
   */
  const handleBookForStudent = async () => {
    if (!selectedStudent) {
      setError('Please select a student first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (selectedSlots.length === 0) {
      setError('Please select at least one slot');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setIsBooking(true);
    setBookingSuccess(false);
    setBookingMessage('');

    try {
      // Prepare slots data for booking
      const slots = selectedSlots.map(slot => ({
        date: slot.dateStr,
        startTime: slot.slotStart,
        endTime: slot.slotEnd
      }));

      // Get provider info from published template
      const providerId = publishedTemplate.userId;
      const providerPhone = publishedTemplate.spPhoneId;

      if (!providerId || !providerPhone) {
        throw new Error('Instructor information is missing');
      }

      console.log('Booking slots for student:', { 
        studentId: selectedStudent.studentId,
        studentName: selectedStudent.name,
        slots, 
        providerId, 
        providerPhone 
      });
      
      // Call booking API with student ID
      const response = await bookingAPI.createMultipleBookingsForStudent(
        { 
          studentId: selectedStudent.studentId,
          slots, 
          providerId, 
          providerPhone 
        },
        getAccessTokenSilently
      );
         
      if (response.success) {
        const { successCount, failCount, failed } = response.data;
        
        if (failCount === 0) {
          setBookingSuccess(true);
          setBookingMessage(
            `Successfully booked ${successCount} slot${successCount > 1 ? 's' : ''} for ${selectedStudent.name}!`
          );
          setSelectedSlots([]); // Clear selected slots
          // Reload existing bookings to update UI
          await loadExistingBookings(publishedTemplate.spPhoneId);
        } else {
          setBookingSuccess(true);
          setBookingMessage(
            `Booked ${successCount} slot${successCount > 1 ? 's' : ''} for ${selectedStudent.name}. ` +
            `${failCount} slot${failCount > 1 ? 's' : ''} failed: ${failed.map(f => f.error).join(', ')}`
          );
          
          // Remove successfully booked slots from selection
          const failedKeys = failed.map(f => 
            `${f.booking.date}-${f.booking.startTime}-${f.booking.endTime}`
          );
          setSelectedSlots(prev => 
            prev.filter(slot => failedKeys.includes(slot.key))
          );
        }

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setBookingSuccess(false);
          setBookingMessage('');
        }, 5000);
      } else {
        throw new Error(response.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError(`Booking failed: ${err.message}`);
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setIsBooking(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // FIXED: toISOString() is UTC — during BST midnight London = 23:00 UTC (previous day).
  // Use local date parts so the saved date always matches what the calendar displays.
  const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const toggleSlotSelection = (date, slotStart, slotEnd) => {
    const slotKey = `${toLocalDateStr(date)}-${slotStart}-${slotEnd}`;
    const slotInfo = {
      key: slotKey,
      date: date,
      dateStr: toLocalDateStr(date),
      dayName: DAYS_OF_WEEK[date.getDay()],
      slotStart,
      slotEnd,
      displayDate: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      displayDay: DAYS_OF_WEEK[date.getDay()]
    };

    setSelectedSlots(prev => {
      const exists = prev.find(s => s.key === slotKey);
      if (exists) {
        return prev.filter(s => s.key !== slotKey);
      } else {
        return [...prev, slotInfo];
      }
    });
  };

  const isSlotSelected = (date, slotStart, slotEnd) => {
    const slotKey = `${toLocalDateStr(date)}-${slotStart}-${slotEnd}`;
    return selectedSlots.some(s => s.key === slotKey);
  };


  // ── Exceptional window selection (one booking per window) ─────────────────
  const isWindowBooked = (date, win) => {
    const key = `${toLocalDateStr(date)}-${win.startTime}-${win.endTime}`;
    return existingBookings.has(key);
  };

  const isWindowSelected = (date, win) => {
    const key = `${toLocalDateStr(date)}-${win.startTime}-${win.endTime}`;
    return selectedSlots.some(s => s.key === key);
  };

  const toggleWindowSelection = (date, win) => {
    const dateStr = toLocalDateStr(date);
    const key     = `${dateStr}-${win.startTime}-${win.endTime}`;
    const slotInfo = {
      key,
      date,
      dateStr,
      dayName:     DAYS_OF_WEEK[date.getDay()],
      slotStart:   win.startTime,
      slotEnd:     win.endTime,
      displayDate: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      displayDay:  DAYS_OF_WEEK[date.getDay()],
      isExceptional: true
    };
    setSelectedSlots(prev => {
      const exists = prev.find(s => s.key === key);
      return exists ? prev.filter(s => s.key !== key) : [...prev, slotInfo];
    });
  };

  // ============================================
  // HELPER FUNCTIONS - CORRECTED TO MATCH BookYourSlots.jsx
  // ============================================

  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const formatTime12hr = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimeRange = (start, end) => {
    return `${formatTime12hr(start)} - ${formatTime12hr(end)}`;
  };

  // CORRECTED: Match BookYourSlots.jsx date range logic
  const isDateInRange = (date) => {
    if (!publishedTemplate) return false;
    const checkDate = new Date(date);
    const from = new Date(publishedTemplate.settings.dateFrom + 'T00:00:00');
    const to   = new Date(publishedTemplate.settings.dateTo   + 'T00:00:00');
    return checkDate >= from && checkDate <= to;
  };

  // CORRECTED: Use dayFrom/dayTo instead of workingDays object
  const isDayEnabled = (date) => {
    if (!publishedTemplate) return false;
    const dayName = DAYS_OF_WEEK[date.getDay()];
    const fromIndex = DAYS_OF_WEEK.indexOf(publishedTemplate.settings.dayFrom);
    const toIndex = DAYS_OF_WEEK.indexOf(publishedTemplate.settings.dayTo);
    const dayIndex = DAYS_OF_WEEK.indexOf(dayName);
    
    if (fromIndex <= toIndex) {
      return dayIndex >= fromIndex && dayIndex <= toIndex;
    } else {
      // Handle wrap-around (e.g., Friday to Monday)
      return dayIndex >= fromIndex || dayIndex <= toIndex;
    }
  };

  // CORRECTED: Use daysInAdvance to check future bookability
  const isDateBookable = (date) => {
    if (!publishedTemplate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const advanceDate = new Date(today);
    advanceDate.setDate(today.getDate() + publishedTemplate.settings.daysInAdvance);
    return checkDate >= today && checkDate <= advanceDate;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // CORRECTED: Match BookYourSlots.jsx private appointment logic
  const isSlotBlocked = (date, slotStart, slotEnd) => {
    if (!publishedTemplate) return { blocked: false };
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin = timeToMinutes(slotEnd);
    
    const dateStr = toLocalDateStr(date);
    const privateAppointments = publishedTemplate.privateAppointments || [];
    
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        // Use timeFrom/timeTo for private appointments
        const aptStartMin = timeToMinutes(apt.timeFrom || apt.startTime);
        const aptEndMin = timeToMinutes(apt.timeTo || apt.endTime);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    
    return { blocked: false };
  };

  const isSlotBooked = (date, slotStart, slotEnd) => {
    const slotKey = `${toLocalDateStr(date)}-${slotStart}-${slotEnd}`;
    return existingBookings.has(slotKey);
  };

  // CORRECTED: Match BookYourSlots.jsx slot generation logic
  
  const generateTimeSlots = () => {
  if (!publishedTemplate) return [];
  
  const slots = [];
  const settings = publishedTemplate.settings;
  const [startHour, startMin] = settings.startTime.split(':').map(Number);
  const [endHour, endMin] = settings.endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const slotDuration = parseInt(settings.slotDuration);
  const breakDuration = parseInt(settings.breakDuration);  // ✅ FIXED: Extract breakDuration
  
  const hasMyBreak = settings.myBreakFrom && settings.myBreakTo;
  const myBreakStartMin = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
  const myBreakEndMin = hasMyBreak ? timeToMinutes(settings.myBreakTo) : null;
  
  let currentMinutes = startMinutes;
  let breakInserted = false;
  
  // ✅ FIXED: Proper loop condition
  while (currentMinutes + slotDuration <= endMinutes) {
    // ✅ FIXED: Handle lunch break (12:00-13:00) properly
    if (hasMyBreak && !breakInserted && currentMinutes + slotDuration > myBreakStartMin) {
      // Add partial slot before break if there's room
      if (currentMinutes < myBreakStartMin) {
        const slotStart = formatMinutesTo24hr(currentMinutes);
        const slotEnd = formatMinutesTo24hr(myBreakStartMin);
        
        if (myBreakStartMin - currentMinutes >= slotDuration) {
          slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
        }
      }
      
      // ✅ FIXED: Add BREAK TIME slot (this will render the break row)
      const breakStart = formatMinutesTo24hr(myBreakStartMin);
      const breakEnd = formatMinutesTo24hr(myBreakEndMin);
      slots.push({ start: breakStart, end: breakEnd, type: 'break' });
      
      currentMinutes = myBreakEndMin;
      breakInserted = true;
      continue;
    }
    
    // Add regular slot
    const slotStart = formatMinutesTo24hr(currentMinutes);
    const slotEndMinutes = currentMinutes + slotDuration;
    const slotEnd = formatMinutesTo24hr(slotEndMinutes);
    
    slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
    
    // ✅ FIXED: Add 15-minute break between slots
    currentMinutes = slotEndMinutes + breakDuration;
  }
  
  return slots;
};


  const getExceptionalEntry = (date) => {
    if (!publishedTemplate) return null;
    const dateStr = toLocalDateStr(date);
    return (publishedTemplate.exceptionalSlots || []).find(es => es.date === dateStr) || null;
  };

  const countWindowSlots = (windows) => (windows || []).length;

  const fmtExcDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatMinutesTo24hr = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Generate data for rendering
  const weekDates = getWeekDates(currentWeekStart);
  const timeSlots = generateTimeSlots();

  // Access control
  if (!isServiceProvider) {
    return (
      <div className="access-denied-container">
        <h2>Access Denied</h2>
        <p>This feature is only accessible to instructors.</p>
      </div>
    );
  }

  // Loading state
  if (isLoading || loadingStudents) {
    return (
      <div className="booking-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (studentsError && students.length === 0) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <Users size={64} className="error-icon" />
          <h2>No Students Found</h2>
          <p className="error-message">{studentsError}</p>
          <p className="error-hint">Please invite students before booking slots for them.</p>
        </div>
      </div>
    );
  }

  if (error && !publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Template Available</h2>
          <p className="error-message">{error}</p>
          <p className="error-hint">Go to Manage Configurations to create and activate a template.</p>
        </div>
      </div>
    );
  }

  if (!publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Schedule Available</h2>
          <p className="error-message">Please create and activate a booking template first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      {/* Student Selector */}
      <div className="student-selector-card">
        <div className="selector-header">
          <Users size={24} className="selector-icon" />
          <h3>Select Student</h3>
        </div>
        <select 
          className="student-dropdown"
          value={selectedStudent?.studentId || ''}
          onChange={(e) => {
            const student = students.find(s => s.studentId === e.target.value);
            setSelectedStudent(student || null);
            setSelectedSlots([]); // Clear selections when changing student
          }}
        >
          <option value="">-- Choose a student --</option>
          {students.map((student) => (
            <option key={student.studentId} value={student.studentId}>
              {student.name} ({student.email})
            </option>
          ))}
        </select>
        
        {selectedStudent && (
          <div className="selected-student-info">
            <p><strong>Booking for:</strong> {selectedStudent.name}</p>
            <p><strong>Email:</strong> {selectedStudent.email}</p>
            <p><strong>Phone:</strong> {selectedStudent.phone}</p>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {bookingSuccess && (
        <div className="booking-success-banner">
          <CheckCircle size={24} />
          <span>{bookingMessage}</span>
        </div>
      )}

      {error && publishedTemplate && (
        <div className="booking-error-banner">
          <AlertCircle size={24} />
          <span>{error}</span>
        </div>
      )}

      {/* Slots Grid */}
      <div className="slots-preview-card">
        <div className="preview-header">
          <h2 className="preview-title">Weekly Schedule</h2>
          <div className="week-navigation">
            <button onClick={() => navigateWeek(-1)} className="nav-button">
              <ChevronLeft size={24} />
            </button>
            <span className="week-label">
              {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={() => navigateWeek(1)} className="nav-button">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-wrapper">
            <div className="grid-header">
              <div className="time-header">Time</div>
              {weekDates.map((date, idx) => {
                const excEntry    = getExceptionalEntry(date);
                const isExc       = !!excEntry;
                const isInRange   = isDateInRange(date);
                const isDayActive = isDayEnabled(date);
                const isBookable  = isDateBookable(date);
                const slotCount = isExc ? (excEntry.windows || []).length : 0;

                if (isExc) {
                  return (
                    <div key={idx} className="day-header day-header-exceptional">
                      <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                      <div className="day-date day-date-exceptional">{date.getDate()}</div>
                      <div className="day-status day-status-exceptional">
                        <Sparkles size={10} /> Exc. ({slotCount})
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                    <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                    <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                    {isInRange && isDayActive && (
                      <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                        {isBookable ? '✓ Open' : 'Future'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="slots-container">
              {timeSlots.map((slot, slotIdx) => (
                <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                  <div className="time-cell">
                    <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                    <div className="time-range">
                      <div className="time-start">{formatTime12hr(slot.start)}</div>
                      <div className="time-end">{formatTime12hr(slot.end)}</div>
                    </div>
                  </div>
                  {slot.type === 'break' ? (
                    <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                      <div className="break-label">
                        <Coffee size={16} />
                        <span>BREAK TIME</span>
                      </div>
                    </div>
                  ) : (
                    weekDates.map((date, dateIdx) => {
                      const excEntry    = getExceptionalEntry(date);
                      const isExc       = !!excEntry;
                      const isInRange   = isDateInRange(date);
                      const isDayActive = isDayEnabled(date);
                      const isBookable  = isDateBookable(date);
                      const blockCheck  = isSlotBlocked(date, slot.start, slot.end);
                      const isSelected  = isSlotSelected(date, slot.start, slot.end);
                      const isBooked    = isSlotBooked(date, slot.start, slot.end);

                      // ── Exceptional date — all cells display as exceptional ──
                      if (isExc) {
                        return (
                          <div key={dateIdx} className="slot-cell slot-exceptional">
                            <div className="slot-status">
                              <div className="status-exceptional"><Sparkles size={10} /> Exc.</div>
                            </div>
                          </div>
                        );
                      }

                      // ── Regular date ──────────────────────────────────────
                      const isAvailable = isInRange && isDayActive && isBookable;
                      return (
                        <div
                          key={dateIdx}
                          className={`slot-cell ${
                            !isAvailable       ? 'slot-unavailable' :
                            blockCheck.blocked ? 'slot-blocked'     :
                            isBooked           ? 'slot-booked'      :
                            isSelected         ? 'slot-selected'    :
                            'slot-available'
                          } ${isAvailable && !blockCheck.blocked && !isBooked ? 'slot-clickable' : ''}`}
                          onClick={() => {
                            if (isAvailable && !blockCheck.blocked && !isBooked) {
                              toggleSlotSelection(date, slot.start, slot.end);
                            }
                          }}
                        >
                          <div className="slot-status">
                            {!isAvailable ? (
                              <div className="status-unavailable">-</div>
                            ) : blockCheck.blocked ? (
                              <div className="status-blocked">{blockCheck.reason}</div>
                            ) : isBooked ? (
                              <div className="status-booked">Booked<br />From {formatTime12hr(slot.start)}</div>
                            ) : (
                              <div className="status-available">
                                {isSelected && <span className="status-check">✓ </span>}
                                {formatTime12hr(slot.start)}<br />{formatTime12hr(slot.end)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            {timeSlots.length === 0 && (
              <div className="empty-state">
                <Clock size={48} className="empty-icon" />
                <p className="empty-text">No time slots available</p>
              </div>
            )}
          </div>
        </div>

        <div className="legend">
          <div className="legend-item">
            <div className="legend-box legend-box-available"></div>
            <span className="legend-text">Available to book</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-blocked"></div>
            <span className="legend-text">Blocked (Break/Private)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-booked"></div>
            <span className="legend-text">Already booked</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-unavailable"></div>
            <span className="legend-text">Outside range/unavailable</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-exceptional"></div>
            <span className="legend-text">Exceptional slot</span>
          </div>
        </div>

        {/* Exceptional Slots — weekly summary */}
        {(() => {
          const weekDateStrs = new Set(weekDates.map(d => toLocalDateStr(d)));
          const weekExcSlots = (publishedTemplate?.exceptionalSlots || [])
            .filter(es => weekDateStrs.has(es.date))
            .sort((a, b) => a.date.localeCompare(b.date));
          if (weekExcSlots.length === 0) return null;
          return (
            <div className="exc-summary-section" style={{ marginTop: '1rem' }}>
              <div className="exc-summary-header">
                <Sparkles size={15} className="exc-summary-icon" />
                <span className="exc-summary-title">Exceptional Slots This Week</span>
                <span className="exc-summary-count">
                  {weekExcSlots.length} date{weekExcSlots.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="exc-summary-rows">
                {weekExcSlots.map((es) => {
                  const excDate = new Date(es.date + 'T00:00:00');
                  const isBookableDate = isDateBookable(excDate);
                  return (
                    <div key={es.date} className="exc-summary-row">
                      <span className="exc-row-date">{fmtExcDate(es.date)}</span>
                      <span className="exc-row-windows">
                        {(es.windows || [])
                          .slice()
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((w, i) => {
                            const booked   = isWindowBooked(excDate, w);
                            const selected = isWindowSelected(excDate, w);
                            const canBook  = isBookableDate && !booked;
                            return (
                              <span
                                key={i}
                                className={`exc-row-win ${
                                  booked   ? 'exc-win-booked'   :
                                  selected ? 'exc-win-selected' :
                                  canBook  ? 'exc-win-bookable' : 'exc-win-future'
                                }`}
                                onClick={() => { if (canBook) toggleWindowSelection(excDate, w); }}
                                title={
                                  booked  ? 'Already booked' :
                                  !canBook ? 'Not yet bookable' :
                                  selected ? 'Click to deselect' : 'Click to select'
                                }
                              >
                                {booked   ? '✓ ' : ''}
                                {selected ? '✓ ' : ''}
                                {formatTime12hr(w.startTime)}–{formatTime12hr(w.endTime)}
                              </span>
                            );
                          })}
                      </span>
                      <span className="exc-row-slots">
                        {(es.windows || []).length} window{(es.windows || []).length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Selected Slots */}
        {selectedSlots.length > 0 && (
          <div className="selected-slots-section">
            <div className="selected-slots-list">
              {selectedSlots.map((slot, idx) => (
                <div key={idx} className="selected-slot-item">
                  {slot.displayDate} – {slot.displayDay}
                  <br />
                  {formatTimeRange(slot.slotStart, slot.slotEnd)}
                </div>
              ))}
            </div>
            <button 
              className="btn-book-now"
              onClick={handleBookForStudent}
              disabled={isBooking || !selectedStudent}
            >
              {isBooking ? 'BOOKING...' : !selectedStudent ? 'SELECT STUDENT FIRST' : `BOOK FOR ${selectedStudent.name.split(' ')[0].toUpperCase()}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}